import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useMenuStore } from './storeMenu'
import {
  copyTable,
  type CopyTableRequest,
  createTable,
  createTableDefinition,
  type CreateTableDefinitionRequest,
  type CreateTableRequest,
  deleteTable,
  fetchTableByDefinitionId,
  fetchTableBySystemObjectId,
  linkTable,
  saveTableColumnDefinition,
  type SaveTableColumnDefinitionRequest,
  toggleColumnIncludeInputFormNew,
  toggleColumnReadOnly,
  unlinkTable
} from '@/api/endpoints/tableDefinitions'
import { updateColumnLabel as apiUpdateColumnLabel } from '@/api/endpoints/tableColumns'
import type { paths } from '@/api/types/auto-generated'
import type { UpdateColumnDefinitionRequest } from '@/api/endpoints/tableColumns'
import { extractErrorMessage } from '@/helpers/errorHandlingHelpers'
import { useSystemMetadataStore } from './systemMetadataStore'
import { useNotificationsStore } from './notificationsStore'
import type { FieldDesignDto } from '@/api/endpoints/systemMetadata'

// Type for table entry basert på swagger-genererte typer
type TableEntry = paths['/api/v2/table']['get']['responses']['200']['content']['application/json']

export const useTableDefinitionsStore = defineStore('tableDefinitions', () => {
  // State
  const tables = ref<Record<number, TableEntry>>({})
  const loading = ref(false)
  const error = ref<string | null>(null)
  const menuStore = useMenuStore()
  const notificationsStore = useNotificationsStore()

  // Race condition prevention
  const pendingRequests = ref<Record<number, Promise<TableEntry | undefined>>>({})

  // Getters
  const getTableByDefinitionId = computed(() => {
    return (id: number) => tables.value[id]
  })

  const getTableBySystemObjectId = computed(() => {
    return (systemObjectId: number) => {
      return Object.values(tables.value).find(table => table.systemObjectId === systemObjectId)
    }
  })

  const getTablesBySystemObjectName = computed(() => {
    return (systemObjectName: string) => {
      return Object.values(tables.value).filter(table => table.systemObjectName === systemObjectName)
    }
  })

  // Actions
  async function fetchByDefinitionId (tableDefinitionId: number): Promise<TableEntry | undefined> {
    if (tables.value[tableDefinitionId]) {
      return tables.value[tableDefinitionId]
    }
    const existingRequest = pendingRequests.value[tableDefinitionId]
    if (existingRequest !== undefined) {
      return existingRequest
    }
    const requestPromise = (async () => {
      loading.value = true
      error.value = null
      try {
        const data = await fetchTableByDefinitionId(tableDefinitionId)
        if (data) {
          tables.value[tableDefinitionId] = data
        }
        return data
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Ukjent feil ved henting av tabell'
        console.error('Feil ved henting av tabell:', err)
        return undefined
      } finally {
        loading.value = false
        delete pendingRequests.value[tableDefinitionId]
      }
    })()
    pendingRequests.value[tableDefinitionId] = requestPromise
    return requestPromise
  }

  async function fetchBySystemObjectId (systemObjectId: number): Promise<TableEntry | undefined> {
    const existingTable = getTableBySystemObjectId.value(systemObjectId)
    if (existingTable) {
      return existingTable
    }
    const pendingKey = -systemObjectId
    const existingRequest = pendingRequests.value[pendingKey]
    if (existingRequest !== undefined) {
      return existingRequest
    }
    const requestPromise = (async () => {
      loading.value = true
      error.value = null
      try {
        const data = await fetchTableBySystemObjectId(systemObjectId)
        if (data?.tableDefinitionId) {
          tables.value[data.tableDefinitionId] = data
        }
        return data
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Ukjent feil ved henting av tabell'
        console.error('Feil ved henting av tabell:', err)
        return undefined
      } finally {
        loading.value = false
        delete pendingRequests.value[pendingKey]
      }
    })()
    pendingRequests.value[pendingKey] = requestPromise
    return requestPromise
  }

  function clearTables () {
    tables.value = {}
    error.value = null
  }

  function refreshTable (tableDefinitionId: number) {
    // Fjern cached versjon for å tvinge ny henting
    delete tables.value[tableDefinitionId]
    delete pendingRequests.value[tableDefinitionId]
  }

  function updateColumnInCache (tableDefinitionId: number, updatedColumn: any) {
    const table = tables.value[tableDefinitionId]
    if (table?.columns) {
      // Finn og oppdater kolonnen
      const columnIndex = table.columns.findIndex(col => col.dbColumnName === updatedColumn.dbColumnName)
      if (columnIndex !== -1) {
        // Oppdater kolonnen med nye verdier
        table.columns[columnIndex] = { ...table.columns[columnIndex], ...updatedColumn }
        console.log('Column updated in cache:', updatedColumn.dbColumnName)
      }
    }
  }

  function addColumnToCache (tableDefinitionId: number, newColumn: any) {
    const table = tables.value[tableDefinitionId]
    if (table?.columns) {
      // Legg til ny kolonne
      table.columns.push(newColumn)
      console.log('Column added to cache:', newColumn.dbColumnName)
    }
  }

  async function prepareColumnForEdit (tableDefinitionId: number, columnName: string) {
    const table = tables.value[tableDefinitionId]
    if (!table?.columns) {
      throw new Error('Table not found')
    }

    const column = table.columns.find(col => col.dbColumnName === columnName)
    if (!column) {
      throw new Error('Column not found')
    }

    return column
  }

  // POST/PUT/DELETE methods from tableDefinitionStore
  const debugPrefix = '[TableDefinitionsStore]'

  async function saveColumnDefinition (
    request: SaveTableColumnDefinitionRequest,
    tableDefinitionId: number
  ): Promise<void> {
    // Send til backend - den returnerer void
    await saveTableColumnDefinition(tableDefinitionId, request)

    // INGEN cache-oppdatering her!
    // Parent-komponenten vil kalle fetchByDefinitionId() etter success
    // for å hente autoritativ data fra backend
  }

  /**
   * Oppdaterer kolonne-definisjon med all business logic i store
   * Returnerer { success: boolean, error?: string }
   *
   * @param request - UpdateColumnDefinitionRequest (samme type som sendes til backend, ingen mapping)
   * @param componentEdit - UI-styring (ikke del av request)
   * @param isReadOnly - UI-styring (ikke del av request)
   */
  async function updateColumnDefinition (
    tableDefinitionId: number,
    request: UpdateColumnDefinitionRequest,
    componentEdit: string,
    isReadOnly: boolean,
    originalPrimaryType: string,
    originalIsReadOnly: boolean
  ): Promise<{ success: boolean; error?: string }> {
    const systemMetadataStore = useSystemMetadataStore();

    try {
      // Validering
      if (!request.primaryFieldType) {
        return { success: false, error: 'Velg primærtype' };
      }

      const hasDefaultValue = request.defaultValue !== null && request.defaultValue !== undefined && (typeof request.defaultValue !== 'string' || request.defaultValue.trim() !== '');
      if (request.nullHandling === 'default' && !hasDefaultValue && !request.defaultFunction) {
        return { success: false, error: 'Standardverdi må settes' };
      }

      // Håndter primary type change hvis nødvendig
      if (request.primaryFieldType !== originalPrimaryType) {
        const design = await systemMetadataStore.resolvePrimary(request.primaryFieldType, {
          maxLength: request.maxLength,
          numericPrecision: request.precision,
          numericScale: request.scale
        });

        const result = await systemMetadataStore.changePrimaryTypeForColumn(
          tableDefinitionId,
          request.columnName ?? '',
          request.primaryFieldType ?? '',
          {
            maxLength: design.storage.maxLength ?? request.maxLength,
            precision: design.storage.precision ?? request.precision,
            scale: design.storage.scale ?? request.scale,
            isNullable: request.nullHandling === 'allow'
          }
        );

        if (!result.isAllowed) {
          return { success: false, error: result.warningMessage || 'Kunne ikke endre primærtype' };
        }
      }

      // Send request direkte til backend (ingen mapping) - backend håndterer all logikk
      await saveColumnDefinition(request, tableDefinitionId);

      // Håndter isReadOnly separat hvis endret (ikke en del av UpdateColumnDefinitionRequest)
      if (isReadOnly !== originalIsReadOnly) {
        await setColumnReadOnly(
          request.columnName ?? '',
          isReadOnly,
          tableDefinitionId,
          true // silent = true for å unngå ekstra notifikasjon
        );
      }

      notificationsStore.addSuccess(`Kolonne ${request.columnName ?? ''} er oppdatert`);
      return { success: true };
    } catch (error: unknown) {
      const errorMessage = extractErrorMessage(error);
      return { success: false, error: errorMessage };
    }
  }

  async function setColumnReadOnly (
    columnName: string,
    newValue: boolean,
    tableDefinitionId: number,
    silent = false
  ): Promise<void> {
    try {
      await toggleColumnReadOnly({ tableDefinitionId, columnName, newValue })

      // Oppdater lokal state etter vellykket API-kall
      const table = tables.value[tableDefinitionId]
      if (table?.columns) {
        const column = table.columns.find((col: any) => col.dbColumnName === columnName)
        if (column) {
          column.isReadOnly = newValue
        }
      }
    } catch (error) {
      console.error(`${debugPrefix} setColumnReadOnly failed:`, error)
      throw error
    }
  }

  async function toggleColumnIncludeInForm (
    columnName: string,
    newValue: boolean,
    tableDefinitionId: number,
    silent = false
  ): Promise<void> {
    try {
      await toggleColumnIncludeInputFormNew({ tableDefinitionId, columnName, newValue })

      // Oppdater lokal state etter vellykket API-kall
      const table = tables.value[tableDefinitionId]
      if (table?.columns) {
        const column = table.columns.find((col: any) => col.dbColumnName === columnName)
        if (column) {
          column.includeInputFormNew = newValue
        }
      }
    } catch (error) {
      console.error(`${debugPrefix} toggleColumnIncludeInForm failed:`, error)
      throw error
    }
  }

  async function deleteTableAction (tableDefinitionId: number): Promise<boolean> {
    try {
      console.log(`${debugPrefix} deleteTableAction called with tableDefinitionId:`, tableDefinitionId)
      await deleteTable({ tableDefinitionId })
      return true
    } catch (error) {
      console.error(`${debugPrefix} deleteTableAction failed:`, error)
      // Re-throw error så den kan håndteres i komponenten
      throw error
    }
  }

  async function unlinkTableAction (systemObjectId: number): Promise<boolean> {
    try {
      await unlinkTable({ systemObjectId })
      // Oppdater menuStore så canDelete computed property oppdateres
      await menuStore.refreshMenusAndTree()
      return true
    } catch (error) {
      console.error(`${debugPrefix} unlinkTableAction failed:`, error)
      return false
    }
  }

  async function linkTableAction (systemObjectId: number, tableDefinitionId: number): Promise<boolean> {
    try {
      await linkTable({ systemObjectId, tableDefinitionId })
      return true
    } catch (error) {
      console.error(`${debugPrefix} linkTableAction failed:`, error)
      return false
    }
  }

  async function CreateTableDefinition (
    dbSchemaName: string,
    dbTableName: string
  ): Promise<{ tableDefinitionId: number }> {
    try {
      const request: CreateTableDefinitionRequest = { dbSchemaName, dbTableName }
      return await createTableDefinition(request)
    } catch (error) {
      console.error(`${debugPrefix} CreateTableDefinition failed:`, error)
      throw error
    }
  }

  async function copyTableAction (
    fromSchemaName: string,
    fromTableName: string,
    toSchemaName: string,
    toTableName: string,
    copyData: boolean,
    systemObjectId?: number | null
  ): Promise<number> {
    try {
      const request: CopyTableRequest = { fromSchemaName, fromTableName, toSchemaName, toTableName, copyData, systemObjectId }
      const response = await copyTable(request)
      return response.tableDefinitionId
    } catch (error) {
      console.error(`${debugPrefix} copyTableAction failed:`, error)
      throw error
    }
  }

  async function createTableAction (
    dbSchemaName: string,
    dbTableName: string,
    columnTypes: CreateTableRequest['columnTypes'] = [],
    systemObjectId?: number | null
  ): Promise<{ tableDefinitionId: number }> {
    try {
      const request: CreateTableRequest = { dbSchemaName, dbTableName, columnTypes, systemObjectId }
      return await createTable(request)
    } catch (error) {
      console.error(`${debugPrefix} createTableAction failed:`, error)
      throw error
    }
  }

  async function updateColumnLabel (
    columnName: string,
    newLabel: string,
    tableDefinitionId: number
  ): Promise<void> {
    // Lagre original verdi for eventuell revert
    const table = tables.value[tableDefinitionId]
    const existingColumn = table?.columns?.find((col: any) => col.dbColumnName === columnName)
    const originalLabel = existingColumn?.columnLabel

    try {
      // Oppdater lokal state først for rask respons
      if (existingColumn) {
        existingColumn.columnLabel = newLabel
      }

      // Send til backend via dedikert label-endpoint
      await apiUpdateColumnLabel(tableDefinitionId, columnName, newLabel)
      notificationsStore.addSuccess(`Visningsnavn endret til "${newLabel}"`)
    } catch (error) {
      console.error(`${debugPrefix} updateColumnLabel failed:`, error)
      // Revert local state on error
      if (existingColumn && originalLabel !== undefined) {
        existingColumn.columnLabel = originalLabel
      }
      notificationsStore.addError('Feil ved oppdatering av visningsnavn', extractErrorMessage(error))
      throw error
    }
  }

  return {
    // State
    tables,
    loading,
    error,

    // Getters
    getTableByDefinitionId,
    getTableBySystemObjectId,
    getTablesBySystemObjectName,

    // Fetch methods
    fetchByDefinitionId,
    fetchBySystemObjectId,

    // Cache management
    clearTables,
    refreshTable,
    updateColumnInCache,
    addColumnToCache,

    // Column editing
    prepareColumnForEdit,

    // POST/PUT/DELETE methods
    saveColumnDefinition,
    updateColumnDefinition,
    updateColumnLabel,
    toggleColumnReadOnly: setColumnReadOnly,
    toggleColumnIncludeInForm,
    deleteTable: deleteTableAction,
    unlinkTable: unlinkTableAction,
    linkTable: linkTableAction,
    CreateTableDefinition,
    copyTable: copyTableAction,
    createTable: createTableAction
  }
})

